// @ts-strict-ignore
import * as braze from "@braze/web-sdk-develop";

import { requestIdleCallback } from "@lib/requestIdleCallback";

import type { BusinessDetailsType } from "../components/FreeTrialRegistration/BusinessDetails";
import type { ProspectDetailsType } from "../components/FreeTrialRegistration/ProspectDetails";
import type { VerifyContactDetailsType } from "../components/FreeTrialRegistration/VerifyContactDetails";
import { getPhoneCodeFromCountryStringCode } from "../components/FreeTrialRegistration/utils";
import type { BrazeSdkEventPropertyType } from "./types";

const brazeSdkConfigs = {
  production: {
    apiKey: "b0ccdf6d-e3c4-4d49-bb3e-ae352c173c99",
    baseUrl: "sdk.iad-01.braze.com",
  },
  development: {
    apiKey: "7d64700d-3ca0-40af-be33-7b29c82cf3d1",
    baseUrl: "sdk.iad-01.braze.com",
  },
};

const brazeSdkConfig =
  new URL(window.location.href).hostname === "try.braze.com"
    ? brazeSdkConfigs.production
    : brazeSdkConfigs.development;

export const useBrazeFreeTrialSdk = () => {
  if (!braze?.isInitialized()) {
    braze.initialize(brazeSdkConfig.apiKey, {
      baseUrl: brazeSdkConfig.baseUrl,
    });
    braze.automaticallyShowInAppMessages();
    braze.openSession();
  }

  const logBrazeFreeTrialEvent = (eventName: string, eventProperties?: BrazeSdkEventPropertyType) => {
    if (braze?.isInitialized()) {
      braze.logCustomEvent(eventName, eventProperties);
    }
  };

  const incrementBrazeFreeTrialAttribute = (attributeName: string, incrementValue: number) => {
    if (braze?.isInitialized()) {
      braze.getUser().incrementCustomUserAttribute(attributeName, incrementValue);
    }
  };

  const appendBrazeFreeTrialAttribute = (attributeName: string, appendValue: string) => {
    if (braze?.isInitialized()) {
      braze.getUser().addToCustomAttributeArray(attributeName, appendValue);
    }
  };

  const trackBrazeFreeTrialPageView = (
    eventName = "pm.page_view_event",
    pageName = document.title,
    pageUrl: string = window.location.origin + window.location.pathname
  ) => {
    logBrazeFreeTrialEvent(eventName, {
      page_name: pageName,
      page_url: pageUrl,
      page_referrer: document.referrer,
    });
    incrementBrazeFreeTrialAttribute("count_of_page_views", 1);
    appendBrazeFreeTrialAttribute("pages_viewed", pageName);
    requestIdleCallback(() => braze.requestImmediateDataFlush());
  };

  const updateBrazeFreeTrialUser = (
    contactDetails: VerifyContactDetailsType,
    prospectDetails: ProspectDetailsType,
    businessDetails: BusinessDetailsType
  ) => {
    if (braze?.isInitialized()) {
      braze.changeUser(contactDetails.email);

      const brazeUser = braze.getUser();
      const phoneNumber = `+${getPhoneCodeFromCountryStringCode(contactDetails.phoneNumberCountry)}${contactDetails.phoneNumber}`;

      brazeUser.setEmail(contactDetails.email);

      brazeUser.setFirstName(prospectDetails.firstName);
      brazeUser.setLastName(prospectDetails.lastName);
      brazeUser.setPhoneNumber(phoneNumber);
      brazeUser.setCustomUserAttribute("job_title", prospectDetails.jobTitle);
      brazeUser.setCustomUserAttribute("company", prospectDetails.company);
      brazeUser.setCustomUserAttribute("department", prospectDetails.department);
      brazeUser.setCustomUserAttribute("phone", phoneNumber);

      brazeUser.setCountry(businessDetails.country);
      brazeUser.setCustomUserAttribute("country", businessDetails.country);
      brazeUser.setCustomUserAttribute("industry", businessDetails.industry);
      brazeUser.setCustomUserAttribute("company_size", businessDetails.companySize);
      brazeUser.setCustomUserAttribute("number_of_users", businessDetails.usersCount);
      brazeUser.setCustomUserAttribute("messaging_channels_of_interest", businessDetails.channels || []);
      brazeUser.setCustomUserAttribute("business_goals", businessDetails.goals || []);
      requestIdleCallback(() => braze.requestImmediateDataFlush());
    }
  };

  return {
    appendBrazeFreeTrialAttribute,
    incrementBrazeFreeTrialAttribute,
    logBrazeFreeTrialEvent,
    trackBrazeFreeTrialPageView,
    updateBrazeFreeTrialUser,
  };
};
